import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, ShoppingBag } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useShop } from "@/context/ShopContext";
import api from "@/lib/api";

export default function Cart() {
  const { t } = useTranslation();
  const { cart, updateQty, removeFromCart, cartSubtotal, format } = useShop();
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const navigate = useNavigate();

  const applyCoupon = async () => {
    try {
      const r = await api.post("/coupons/validate", null, { params: { code: coupon } });
      setAppliedCoupon(r.data); toast.success(`Coupon ${r.data.code} applied (-${r.data.percent_off}%)`);
    } catch { toast.error("Invalid coupon"); }
  };

  const discount = appliedCoupon ? cartSubtotal * (appliedCoupon.percent_off / 100) : 0;
  const shipping = (cartSubtotal - discount) >= 150 ? 0 : 9.9;
  const total = Math.max(0, cartSubtotal - discount + shipping);

  if (cart.length === 0) {
    return (
      <section className="py-24 text-center">
        <ShoppingBag size={48} weight="thin" className="mx-auto text-[var(--bm-muted)]"/>
        <h1 className="font-serif-bm text-3xl mt-4">{t("cart.empty")}</h1>
        <Link to="/shop" className="btn-primary mt-8 inline-flex">{t("cart.continue")}</Link>
      </section>
    );
  }

  return (
    <section className="py-12 lg:py-16" data-testid="cart-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h1 className="font-serif-bm text-3xl md:text-4xl mb-8">{t("cart.title")}</h1>
          <div className="space-y-5">
            {cart.map(item => (
              <div key={item.product_id} className="card-soft p-4 flex gap-4" data-testid={`cart-row-${item.product_id}`}>
                <Link to={`/product/${item.slug}`} className="w-24 h-28 bg-[#f4f4f4] rounded-xl overflow-hidden shrink-0">
                  <img src={item.image} alt="" className="w-full h-full object-cover"/>
                </Link>
                <div className="flex-1">
                  <div className="font-serif-bm text-lg">{item.name}</div>
                  <div className="text-sm mt-1">{format(item.price)}</div>
                  <div className="flex items-center gap-4 mt-4">
                    <div className="flex items-center border border-[var(--bm-border)] rounded-full">
                      <button onClick={()=>updateQty(item.product_id, item.quantity - 1)} className="w-8 h-8" data-testid={`cart-page-dec-${item.product_id}`}>−</button>
                      <span className="text-sm min-w-6 text-center">{item.quantity}</span>
                      <button onClick={()=>updateQty(item.product_id, item.quantity + 1)} className="w-8 h-8" data-testid={`cart-page-inc-${item.product_id}`}>+</button>
                    </div>
                    <button onClick={()=>removeFromCart(item.product_id)} className="text-xs underline text-[var(--bm-muted)]">{t("cart.remove")}</button>
                  </div>
                </div>
                <div className="font-medium text-sm">{format(item.price * item.quantity)}</div>
              </div>
            ))}
          </div>
        </div>
        <aside className="card-soft p-6 h-fit sticky top-24" data-testid="cart-summary">
          <h3 className="font-serif-bm text-xl mb-5">Order Summary</h3>
          <div className="flex gap-2 mb-5">
            <input className="bm-input flex-1" placeholder={t("cart.coupon")} value={coupon} onChange={e=>setCoupon(e.target.value)} data-testid="coupon-input"/>
            <button onClick={applyCoupon} className="btn-secondary !py-2.5 !px-4" data-testid="coupon-apply-btn">{t("cart.apply")}</button>
          </div>
          <div className="text-sm space-y-2 border-t border-[var(--bm-border)] pt-4">
            <div className="flex justify-between"><span>{t("cart.subtotal")}</span><span data-testid="summary-subtotal">{format(cartSubtotal)}</span></div>
            {appliedCoupon && <div className="flex justify-between text-[var(--bm-gold)]"><span>Discount</span><span>-{format(discount)}</span></div>}
            <div className="flex justify-between"><span>{t("cart.shipping")}</span><span>{shipping === 0 ? "FREE" : format(shipping)}</span></div>
            <div className="flex justify-between text-base font-medium border-t border-[var(--bm-border)] pt-3 mt-3"><span>{t("cart.total")}</span><span data-testid="summary-total">{format(total)}</span></div>
          </div>
          <button onClick={()=>navigate("/checkout", { state: { coupon: appliedCoupon?.code } })} className="btn-primary w-full justify-center mt-6" data-testid="proceed-checkout-btn">
            <ShieldCheck size={16} weight="thin"/> {t("cart.checkout")}
          </button>
          <p className="text-[10px] tracking-widest uppercase text-center mt-4 text-[var(--bm-muted)]">SSL Encrypted · 30-day return</p>
        </aside>
      </div>
    </section>
  );
}
